/**
 * The Design Builder shell: toolbar, three panes, keyboard map, persistence.
 *
 * Owns the two stores (provided here, not in root, so leaving the mode disposes
 * the document) and wires the panels to them. Everything structural lives in
 * the panels; this file is orchestration.
 */

import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Injector,
  afterNextRender,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, debounceTime, firstValueFrom, takeUntil } from 'rxjs';

import { KeycloakService } from 'keycloak-angular';
import { ActivatedRoute, Router } from '@angular/router';
import { SelfServiceConfigService } from '../shared/self-service-config.service';
import { SelfServiceDashboardService } from '../shared/self-service-dashboard.service';
import { dashboardSlug } from '../dvm-layout-builder/dvm-dashboard-layout-json.utils';
import { markDashboardJustPublished, readDynamicDashboardCache, writeDynamicDashboardCache } from '../shared/self-service-storage.utils';
import { isPlatformCommitFailure } from '../shared/dashboard-layouts-envelope.utils';
import type { BuilderLayoutConfig } from '../shared/self-service-config.types';
import {
  DVM_LAYOUT_BUILDER_DEFAULT_LAYOUT,
  layoutTypesOf,
} from '../dvm-layout-builder/dvm-builder-config.utils';

import {
  DRAFT_SAVE_DEBOUNCE_MS,
  FALLBACK_LAYOUT_TYPES,
  buildPalette,
} from './design-builder.constants';
import type {
  DesignDocument,
  DesignNode,
  DesignProject,
  DesignTool,
  PaletteItem,
  TextDataBinding,
  WidgetContent,
} from './design-builder.types';
import { DesignBuilderPersistenceService } from './services/design-builder-persistence.service';
import { DesignDataService, formatBoundValue } from './services/design-data.service';
import { DesignDataPanelComponent, type DataColumnPick } from './panels/data-panel/design-data-panel.component';
import type { DashboardTile } from '../dvm-layout-builder/dvm-layout-builder.types';
import { DesignRuntimeService } from './data/design-runtime.service';
import { zoneSliderBindingOf } from './renderers/zone-slider-binding.utils';
import { storedBuild, tileDataset, tileRenderData } from './data/design-tiles.utils';
import { pruneSupersededOverrides } from '../components/nested-table/nested-table.utils';
import { DesignCanvasStore } from './state/design-canvas.store';
import { DesignDocumentStore } from './state/design-document.store';
import { createEmptyDocument, parseDesignDocument, slugify } from './utils/design-json.utils';
import { buildPublishedLayoutJson, hasSampleData, isLiveOnlyWidget, withoutStoredRows, publishedDashboardId, publishedDesignId, pageScreensOf, publishedLayoutAsDocumentJson, screensOf, type DesignScreen } from './utils/design-publish.utils';
import { DesignCanvasComponent } from './panels/canvas/design-canvas.component';
import { DesignScreenPopupComponent } from './renderers/design-screen-popup.component';
import { DesignScreenPreviewComponent } from './renderers/design-screen-preview.component';
import { ComponentPaletteComponent } from './panels/component-palette/component-palette.component';
import { DesignInspectorComponent } from './panels/inspector/design-inspector.component';
import {
  LayersPanelComponent,
  type LayerDropEvent,
} from './panels/layers-panel/layers-panel.component';
import { DesignJsonPanelComponent } from './panels/json-panel/design-json-panel.component';
import { IconLibraryComponent } from './panels/icon-library/icon-library.component';
import { createTextComponentId, locateNode } from './utils/design-node.utils';
import { createDocumentId } from './utils/design-node.utils';
import { templateThumbnailDataUrl } from './utils/design-template-thumbnail.utils';
import { DesignSaveAsDialogComponent } from './dialogs/design-save-as-dialog.component';
import {
  DesignOpenDialogComponent,
  type DesignOpenDialogData,
  type DesignOpenDialogResult,
} from './dialogs/design-open-dialog.component';
import {
  DesignProjectDialogComponent,
  type DesignProjectDialogData,
} from './dialogs/design-project-dialog.component';

interface TemplateGroup {
  /** projectId, or '' for templates without a project. */
  key: string;
  name: string;
  templates: Record<string, unknown>[];
}

const PANE_COLLAPSED_KEY = 'dsg.paneCollapsed.';

function readPaneCollapsed(side: 'left' | 'right'): boolean {
  try { return localStorage.getItem(PANE_COLLAPSED_KEY + side) === '1'; } catch { return false; }
}

function writePaneCollapsed(side: 'left' | 'right', collapsed: boolean): void {
  try { localStorage.setItem(PANE_COLLAPSED_KEY + side, collapsed ? '1' : '0'); } catch { /* storage blocked */ }
}

const PANE_WIDTH_KEY = 'dsg.paneWidth.';
const PANE_DEFAULT_WIDTH = { left: 260, right: 280 } as const;
const PANE_MIN_WIDTH = 220;

/** Never narrower than the controls need, never wider than half the window. */
function clampPaneWidth(width: number): number {
  const max = Math.max(PANE_MIN_WIDTH, Math.round(window.innerWidth * 0.5));
  return Math.min(max, Math.max(PANE_MIN_WIDTH, Math.round(width)));
}

function readPaneWidth(side: 'left' | 'right'): number {
  try {
    const stored = Number(localStorage.getItem(PANE_WIDTH_KEY + side));
    return stored ? clampPaneWidth(stored) : PANE_DEFAULT_WIDTH[side];
  } catch {
    return PANE_DEFAULT_WIDTH[side];
  }
}

function writePaneWidth(side: 'left' | 'right', width: number): void {
  try { localStorage.setItem(PANE_WIDTH_KEY + side, String(width)); } catch { /* storage blocked */ }
}

type RightTab = 'design' | 'data' | 'json';

/** Toolbar tools that map onto a palette key. */
const TOOL_KEYS: Record<string, DesignTool> = {
  v: 'select',
  h: 'hand',
  f: 'frame',
  r: 'rect',
  o: 'ellipse',
  l: 'line',
  t: 'text',
};

@Component({
  selector: 'app-design-builder',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
    MatSnackBarModule,
    DesignCanvasComponent,
    DesignScreenPreviewComponent,
    DesignScreenPopupComponent,
    ComponentPaletteComponent,
    DesignInspectorComponent,
    LayersPanelComponent,
    DesignJsonPanelComponent,
    IconLibraryComponent,
    DesignDataPanelComponent,
  ],
  providers: [DesignDocumentStore, DesignCanvasStore, DesignBuilderPersistenceService, DesignDataService, DesignRuntimeService],
  templateUrl: './design-builder.component.html',
  styleUrl: './design-builder.component.scss',
})
export class DesignBuilderComponent implements OnInit, OnDestroy {
  protected readonly doc = inject(DesignDocumentStore);
  protected readonly view = inject(DesignCanvasStore);
  private readonly persistence = inject(DesignBuilderPersistenceService);
  private readonly dataService = inject(DesignDataService);
  private readonly runtime = inject(DesignRuntimeService);
  private readonly configService = inject(SelfServiceConfigService);
  private readonly dashboardService = inject(SelfServiceDashboardService);
  private readonly keycloak = inject(KeycloakService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly snackBar = inject(MatSnackBar);
  private readonly dialog = inject(MatDialog);
  private readonly injector = inject(Injector);

  private readonly destroy$ = new Subject<void>();
  private readonly draftSave$ = new Subject<void>();

  private readonly canvasRef = viewChild<DesignCanvasComponent>(DesignCanvasComponent);
  private readonly jsonPanelRef = viewChild<DesignJsonPanelComponent>(DesignJsonPanelComponent);
  private readonly templateThumbnailCache = new WeakMap<Record<string, unknown>, string>();
  private readonly templateScreenCache = new WeakMap<Record<string, unknown>, DesignScreen | null>();

  // ── UI state ───────────────────────────────────────────────────────────────
  readonly rightTab = signal<RightTab>('design');
  readonly templates = signal<Record<string, unknown>[]>([]);
  readonly templatesLoading = signal(false);
  readonly templatesError = signal('');
  /** Name last loaded from or written to the database; used to detect a manual rename. */
  readonly persistedDesignName = signal<string | null>(null);
  readonly activeDesignId = computed(() => this.doc.document().id);
  readonly leftTab = signal<'components' | 'icons'>('components');
  readonly saving = signal(false);
  readonly previewMode = signal(false);
  /** Where the Preview overlay starts: just below the app bar, right of any rail. */
  readonly previewInset = signal({ top: 0, left: 0 });
  private readonly builderRoot = viewChild<ElementRef<HTMLElement>>('builderRoot');
  readonly previewScreenId = signal<string | null>(null);
  readonly leftPaneWidth = signal(readPaneWidth('left'));
  readonly rightPaneWidth = signal(readPaneWidth('right'));
  readonly leftPaneCollapsed = signal(readPaneCollapsed('left'));
  readonly rightPaneCollapsed = signal(readPaneCollapsed('right'));
  /** The pane whose edge is being dragged — turns off the width transition. */
  readonly resizingPane = signal<'left' | 'right' | null>(null);

  togglePane(side: 'left' | 'right'): void {
    const pane = side === 'left' ? this.leftPaneCollapsed : this.rightPaneCollapsed;
    pane.update((collapsed) => !collapsed);
    writePaneCollapsed(side, pane());
  }

  /** Drag a pane's inner edge to resize it; the width is remembered. */
  startPaneResize(side: 'left' | 'right', event: PointerEvent): void {
    if (event.button !== 0) return;
    event.preventDefault();
    const width = side === 'left' ? this.leftPaneWidth : this.rightPaneWidth;
    const startX = event.clientX;
    const startWidth = width();
    this.resizingPane.set(side);

    const onMove = (move: PointerEvent) => {
      const delta = move.clientX - startX;
      // The left pane grows rightwards, the right pane leftwards.
      width.set(clampPaneWidth(startWidth + (side === 'left' ? delta : -delta)));
    };
    const onEnd = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onEnd);
      window.removeEventListener('pointercancel', onEnd);
      this.resizingPane.set(null);
      writePaneWidth(side, width());
      // The canvas changed width: let charts re-fit.
      window.dispatchEvent(new Event('resize'));
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onEnd);
    window.addEventListener('pointercancel', onEnd);
  }

  /** Double-click the handle: back to the default width. */
  resetPaneWidth(side: 'left' | 'right'): void {
    const width = side === 'left' ? this.leftPaneWidth : this.rightPaneWidth;
    width.set(PANE_DEFAULT_WIDTH[side]);
    writePaneWidth(side, width());
    window.dispatchEvent(new Event('resize'));
  }

  /** Shape tools, listed once for both the inline buttons and the menu. */
  readonly shapeTools: ReadonlyArray<{ key: DesignTool; icon: string; label: string }> = [
    { key: 'frame', icon: 'crop_free', label: 'Frame (F)' },
    { key: 'rect', icon: 'rectangle', label: 'Rectangle (R)' },
    { key: 'ellipse', icon: 'circle', label: 'Ellipse (O)' },
    { key: 'line', icon: 'horizontal_rule', label: 'Line (L)' },
    { key: 'text', icon: 'title', label: 'Text (T)' },
  ];

  readonly isShapeToolActive = computed(() =>
    this.shapeTools.some((tool) => tool.key === this.activeTool()),
  );

  /** The collapsed button shows the armed shape, so the menu is not a mystery. */
  readonly shapeToolIcon = computed(
    () => this.shapeTools.find((tool) => tool.key === this.activeTool())?.icon ?? 'category',
  );

  readonly palette = signal<PaletteItem[]>(buildPalette(FALLBACK_LAYOUT_TYPES));
  /** The merged DVM builder config — source of the inspector's chart controls. */
  readonly layoutConfig = signal<BuilderLayoutConfig | null>(null);

  // ── Projects (the parent that groups dashboards) ──────────────────────────
  readonly projects = signal<DesignProject[]>([]);
  readonly currentProjectId = computed(() => this.doc.document().projectId ?? '');

  private loadProjects(): void {
    this.persistence
      .listProjects()
      .pipe(takeUntil(this.destroy$))
      .subscribe((projects) => this.projects.set(projects));
  }

  onProjectChange(projectId: string): void {
    const project = this.allProjects().find((candidate) => candidate.id === projectId);
    this.doc.setProject(projectId, project?.name ?? '');
  }

  createProject(): void {
    this.dialog.open<DesignProjectDialogComponent, DesignProjectDialogData, string | null>(DesignProjectDialogComponent, {
      width: '460px',
      maxWidth: 'calc(100vw - 32px)',
      autoFocus: 'dialog',
      restoreFocus: true,
      data: { existingNames: this.allProjects().map((project) => project.name) },
    }).afterClosed().pipe(takeUntil(this.destroy$)).subscribe((name) => {
      if (!name) return;
      this.persistence
        .createProject(name)
        .pipe(takeUntil(this.destroy$))
        .subscribe((project) => {
          this.projects.update((list) => [...list, project]);
          this.doc.setProject(project.id, project.name);
          this.notify(`Project "${project.name}" created`);
        });
    });
  }

  /**
   * Projects from the projects API plus every project a saved template is
   * filed under, so the picker offers each folder shown in Templates.
   */
  readonly allProjects = computed<DesignProject[]>(() => {
    const byId = new Map<string, DesignProject>();
    for (const project of this.projects()) byId.set(project.id, project);
    for (const template of this.templates()) {
      const id = String(template['projectId'] ?? '').trim();
      if (!id || byId.has(id)) continue;
      const name = String(template['projectName'] ?? '').trim() || id;
      byId.set(id, { id, name, slug: slugify(name), createdAt: '', updatedAt: '' });
    }
    // The open dashboard's project must stay selectable even before anything loads.
    const document = this.doc.document();
    if (document.projectId && !byId.has(document.projectId)) {
      const name = document.projectName || document.projectId;
      byId.set(document.projectId, { id: document.projectId, name, slug: slugify(name), createdAt: '', updatedAt: '' });
    }
    return [...byId.values()].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  });

  readonly designName = computed(() => this.doc.document().name);
  readonly status = computed(() => this.doc.document().meta.status);
  readonly zoomPercent = this.view.zoomPercent;
  readonly activeTool = this.view.tool;
  readonly nodes = this.doc.nodes;
  readonly selectedIds = this.doc.selectedIds;
  readonly hasSelection = computed(() => this.selectedIds().size > 0);
  readonly canGroup = computed(() => this.selectedIds().size > 1);

  // ── Layout grid ────────────────────────────────────────────────────────────
  readonly layoutGrid = this.view.layoutGrid;
  readonly grid = computed(() => this.doc.canvas().grid);
  readonly gridSizes: readonly number[] = [4, 8, 10, 16, 20, 24, 32];

  /** Showing the grid also turns snapping on — lining things up is the point. */
  toggleLayoutGrid(): void {
    const show = !this.layoutGrid();
    this.view.setLayoutGrid(show);
    if (show && !this.grid().snap) this.doc.setGrid({ snap: true });
  }

  setGridSize(size: number): void {
    this.view.setLayoutGrid(true);
    if (size !== this.grid().size) this.doc.setGrid({ size });
  }

  toggleGridSnap(): void {
    this.doc.setGrid({ snap: !this.grid().snap });
  }

  constructor() {
    // Autosave the draft whenever the document changes.
    effect(() => {
      this.doc.revision();
      this.draftSave$.next();
    });

    // Buttons do not navigate away from the design while it is being edited.
    effect(() => {
      const editing = !this.previewMode();
      untracked(() => this.runtime.designMode.set(editing));
    });

    // A Navigate → Screen button, clicked in Preview.
    effect(() => {
      const request = this.runtime.screenRequest();
      if (!request || !this.previewMode()) return;
      if (this.screens().some((screen) => screen.id === request.screenId)) {
        untracked(() => this.previewScreenId.set(request.screenId));
      }
    });
  }

  ngOnInit(): void {
    this.loadPalette();
    this.loadProjects();
    // Templates also feed the project picker, so fetch them up front.
    this.refreshTemplates();
    this.restoreDraft();
    void this.openPublishedFromRoute();

    this.draftSave$
      .pipe(debounceTime(DRAFT_SAVE_DEBOUNCE_MS), takeUntil(this.destroy$))
      .subscribe(() => {
        this.persistence.saveDraft(this.doc.document()).subscribe();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Bootstrap ──────────────────────────────────────────────────────────────

  /** Palette widgets come from the SAME config file the Layout Builder reads,
   *  so a layoutType added there shows up here with no code change. */
  private loadPalette(): void {
    this.configService
      .loadBuilderLayout('dvm-layout', DVM_LAYOUT_BUILDER_DEFAULT_LAYOUT)
      .pipe(takeUntil(this.destroy$))
      .subscribe((layout) => {
        // Kept, not discarded: the same config drives the inspector's variant
        // and format controls, so the Design Builder never restates them.
        this.layoutConfig.set(layout);
        this.dataService.setLayout(layout);
        this.runtime.setLayout(layout);
        const types = layoutTypesOf(layout);
        if (types.length) this.palette.set(buildPalette(types));
      });
  }

  /**
   * `?dashboard=<slug>` — Back to Builder / Edit on a published design — opens
   * that dashboard, the way the Layout Builder reopens its own. The layout comes
   * from the navigation state, then the dynamic cache, then the API.
   */
  private async openPublishedFromRoute(): Promise<void> {
    const slug = this.route.snapshot.queryParamMap.get('dashboard');
    if (!slug) return;
    const handed = (history.state?.['layoutJson'] ?? null) as Record<string, unknown> | null;
    const layoutJson = handed
      ?? readDynamicDashboardCache<Record<string, unknown> | null>(slug, null)
      ?? await firstValueFrom(this.dashboardService.getPublishedLayoutBySlug(slug, await this.userEmail()));
    if (!layoutJson || layoutJson['builder'] !== 'design') {
      this.notify('That dashboard was not made in the Design Builder.');
      return;
    }
    // The saved design holds the full editable tree; the published row only
    // its screens, rebuilt into frames when the design is not in the store.
    // Both are keyed by the dashboard id the published row carries.
    const dashboardId = String(layoutJson['dashboardId'] ?? '').trim();
    const saved = dashboardId ? await this.savedTemplate(dashboardId) : null;
    const { document } = parseDesignDocument(JSON.stringify(saved ?? publishedLayoutAsDocumentJson(layoutJson)));
    if (!document) {
      this.notify('Could not open that dashboard.');
      return;
    }
    this.doc.resetDocument(document);
    this.doc.setStatus('published');
    this.persistedDesignName.set(document.name);
  }

  /** The saved design behind a published dashboard, by its dashboard id. */
  private async savedTemplate(dashboardId: string): Promise<Record<string, unknown> | null> {
    try {
      return await firstValueFrom(this.persistence.loadTemplate(dashboardId));
    } catch {
      return null;
    }
  }

  /** Pick up an unsaved draft so a reload does not lose work. */
  private restoreDraft(): void {
    const draft = this.persistence.readDraft();
    if (draft) this.doc.resetDocument(draft);
  }

  // ── Toolbar ────────────────────────────────────────────────────────────────

  setTool(tool: DesignTool | string): void {
    this.view.setTool(tool as DesignTool);
  }

  onPaletteItemPicked(item: PaletteItem): void {
    // Arm the item so the next canvas drag draws it at the chosen size.
    this.view.setTool(item.key as DesignTool);
  }

  /**
   * Which phase of the job you are in: laying the screen out, or connecting it
   * to data. A top-level switch rather than a side tab, because it changes what
   * the CANVAS shows too — in data mode unbound components grey out, so what is
   * still outstanding is visible at a glance.
   */
  readonly mode = signal<'design' | 'data'>('design');

  setMode(next: 'design' | 'data'): void {
    this.mode.set(next);
    this.setRightTab(next);
  }

  /** Components still showing generated sample data. */
  readonly unboundCount = computed(() => {
    this.doc.revision();
    return countSampleWidgets(this.doc.document());
  });

  setRightTab(tab: RightTab): void {
    this.rightTab.set(tab);
    if (tab === 'data') this.dataService.loadDatasets();
  }

  /** Templates open in a dialog: picking one REPLACES the document, which is a
   *  file action, not a property of the current selection. */
  openTemplatesDialog(): void {
    this.refreshTemplates();
    this.dialog
      .open<DesignOpenDialogComponent, DesignOpenDialogData, DesignOpenDialogResult>(DesignOpenDialogComponent, {
        width: '920px',
        maxWidth: 'calc(100vw - 48px)',
        panelClass: 'dsg-open-dialog-panel',
        autoFocus: false,
        restoreFocus: true,
        data: {
          groups: this.templateGroups(),
          activeId: this.activeDesignId(),
          loading: this.templatesLoading(),
          error: this.templatesError(),
          previewScreen: (template) => this.templatePreviewScreen(template),
          thumbnail: (template) => this.templateThumbnail(template),
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result?.action === 'open') this.openTemplate(result.template);
        else if (result?.action === 'refresh') this.openTemplatesDialog();
      });
  }

  /**
   * Data tab → selected text: bind it to that column. A text with no value
   * token becomes just the value (its old content was a placeholder); one
   * that already has the token keeps its wording. Format settings survive a
   * column swap.
   */
  bindSelectedText(pick: DataColumnPick): void {
    const node = this.doc.selectedNode();
    if (node?.type !== 'TEXT' || !node.text) return;
    const previous = node.dataBinding;
    const template = node.text.value.includes('{{value}}') ? node.text.value : '{{value}}';
    // The componentId is assigned on BIND, not on Compute: without it the text
    // has nothing to key its computed value off, and a published screen can
    // never fetch it. Compute just refreshes a value it can already address.
    const componentId = previous?.componentId || createTextComponentId();
    this.doc.updateNode(node.id, {
      dataBinding: { ...(previous ?? {}), dataset: pick.dataset, field: pick.field, aggregation: pick.aggregation || 'SUM', componentId },
      text: { ...node.text, value: template },
    }, 'Bind text to data');
  }

  /**
   * Register a bound text as a KPI component.
   *
   * It gets a real componentId — not the `text:` marker, which the backend
   * never sees — so it can be saved to the component store, sent as a compute
   * target and recomputed with every filter, exactly like a KPI tile.
   */
  connectSelectedText(event: { nodeId: string; binding: TextDataBinding }): void {
    const node = locateNode(this.doc.nodes(), event.nodeId)?.node;
    if (node?.type !== 'TEXT') return;
    const componentId = node.dataBinding?.componentId || createTextComponentId();
    const binding: TextDataBinding = { ...(node.dataBinding ?? {}), ...event.binding, componentId };
    this.doc.updateNode(event.nodeId, { dataBinding: binding }, 'Connect text to data');
    this.runtime.connectText(componentId, binding, node.name ?? '');
  }

  /**
   * The Data tab's build form generated (or updated) this widget: its data
   * becomes real data — no longer sample, so publishing keeps it — and the
   * build is stored so re-selecting the widget reopens the same form.
   */
  applyWidgetBuild(nodeId: string, tile: DashboardTile): void {
    const node = locateNode(this.doc.nodes(), nodeId)?.node;
    if (!node?.widget) return;
    const data = tileRenderData(tile);
    const dataset = tileDataset(tile);
    const layoutType = tile.layoutType || node.widget.layoutType;
    // A nested table keeps no rows in the design (they always come from the
    // compute API); the ones just generated show from memory until reload.
    const liveOnly = isLiveOnlyWidget({ layoutType, sample: false });
    this.runtime.forget(nodeId);
    this.doc.updateNode(nodeId, {
      widget: {
        ...node.widget,
        appearance: supersedeNestedOverrides(node.widget, tile),
        ...(data ? { data: liveOnly ? withoutStoredRows(data) : data, sample: false } : {}),
        layoutType,
        build: storedBuild(tile),
        componentId: tile.componentId,
        binding: { ...(node.widget.binding ?? {}), ...(dataset ? { dataset } : {}) },
      },
    }, 'Generate widget data');
    // Same for a dropdown's options: saved ones are dropped, so keep these in memory.
    if ((liveOnly && data) || layoutType === 'DROPDOWN') this.runtime.preview(tile);
  }

  /**
   * The Data tab rewrote a widget's data-tier binding (zone slider: value /
   * range / zones columns). The binding is resolved client-side from dataset
   * rows at render time, so nothing else on the widget changes.
   */
  applyWidgetBinding(nodeId: string, binding: Record<string, unknown>): void {
    const node = locateNode(this.doc.nodes(), nodeId)?.node;
    if (!node?.widget) return;
    this.doc.updateNode(nodeId, { widget: { ...node.widget, binding, sample: false } }, 'Bind widget to data');
  }

  /**
   * Zone slider Generate / Update Configuration: give the widget a stable
   * componentId (the compute registration key, kept on republish), store the
   * mapping, then register each mapped measure with the compute API. The
   * runtime holds the answers; the host overlays them on the slider.
   */
  generateZoneSlider(nodeId: string, binding: Record<string, unknown>): void {
    const node = locateNode(this.doc.nodes(), nodeId)?.node;
    if (!node?.widget) return;
    const componentId = node.widget.componentId || `zone-slider-${nodeId.replace(/^nd_/, '')}`;
    this.doc.updateNode(nodeId, { widget: { ...node.widget, binding, componentId, sample: false } }, 'Generate zone slider');
    this.runtime.connectZoneSlider(nodeId, componentId, zoneSliderBindingOf(binding), node.name ?? '');
  }

  /** Unlinking keeps what the text currently shows instead of a bare token. */
  unbindSelectedText(): void {
    const node = this.doc.selectedNode();
    const binding = node?.dataBinding;
    if (node?.type !== 'TEXT' || !node.text || !binding) return;
    const state = this.dataService.value(binding, node.id);
    const shown = state.status === 'ready' ? formatBoundValue(binding, state.value) : '';
    this.doc.updateNode(node.id, {
      dataBinding: undefined,
      text: { ...node.text, value: node.text.value.split('{{value}}').join(shown) },
    }, 'Unlink text from data');
  }

  refreshTemplates(): void {
    if (this.templatesLoading()) return;
    this.templatesLoading.set(true);
    this.templatesError.set('');
    this.persistence.listTemplates().pipe(takeUntil(this.destroy$)).subscribe({
      next: (templates) => {
        this.templates.set(templates);
        this.templatesLoading.set(false);
      },
      error: () => {
        this.templatesLoading.set(false);
        this.templatesError.set('Could not load templates. Check the backend connection and try again.');
      },
    });
  }

  /**
   * Open a dashboard from the Templates dialog.
   *
   * The listed row is enough to identify it, but the dashboard is fetched by
   * its id before opening, so what lands on the canvas is what the backend
   * holds now rather than whatever the list returned.
   */
  openTemplate(template: Record<string, unknown>): void {
    const { document, error } = parseDesignDocument(JSON.stringify(template));
    if (!document) {
      this.notify(`This template cannot be opened in Design Builder. ${error ?? ''}`);
      return;
    }
    this.persistence
      .loadTemplate(publishedDashboardId(document))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (fetched) => this.applyTemplate(fetched ? parseDesignDocument(JSON.stringify(fetched)).document ?? document : document),
        // The list already gave us a complete design; a failed fetch is no
        // reason to refuse to open it.
        error: () => this.applyTemplate(document),
      });
  }

  private applyTemplate(document: DesignDocument): void {
    this.doc.setDocument(document, 'Open template');
    this.persistedDesignName.set(document.name);
    this.view.reset();
    this.jsonPanelRef()?.revert();
    // Fit the page's WIDTH, pinned to the top (as a fresh canvas opens). Fitting
    // the whole artboard made a tall page (e.g. 10592px) open at ~10%.
    afterNextRender(() => this.view.fitToWidth(), { injector: this.injector });
    this.notify(`Opened "${document.name}"`);
  }

  trackTemplate(_index: number, template: Record<string, unknown>): unknown { return template['id']; }

  /** Templates bucketed by project: the open dashboard's project first, then A→Z, "No project" last. */
  readonly templateGroups = computed<TemplateGroup[]>(() => {
    const projectNames = new Map(this.projects().map((project) => [project.id, project.name]));
    const groups = new Map<string, TemplateGroup>();
    for (const template of this.templates()) {
      const key = String(template['projectId'] ?? '').trim();
      let group = groups.get(key);
      if (!group) {
        const name = key
          ? projectNames.get(key) || String(template['projectName'] ?? '').trim() || key
          : 'No project';
        group = { key, name, templates: [] };
        groups.set(key, group);
      }
      group.templates.push(template);
    }
    const current = this.currentProjectId();
    return [...groups.values()].sort((a, b) => {
      const aCurrent = !!current && a.key === current;
      const bCurrent = !!current && b.key === current;
      if (aCurrent !== bCurrent) return aCurrent ? -1 : 1;
      if (!a.key !== !b.key) return a.key ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  });

  /** Folders start closed; only the ones the user opens are tracked. */
  private readonly expandedTemplateGroups = signal<ReadonlySet<string>>(new Set());

  isTemplateGroupCollapsed(key: string): boolean {
    return !this.expandedTemplateGroups().has(key);
  }

  toggleTemplateGroup(key: string): void {
    this.expandedTemplateGroups.update((current) => {
      const next = new Set(current);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  }

  trackTemplateGroup(_index: number, group: TemplateGroup): string { return group.key; }

  templatePreviewScreen(template: Record<string, unknown>): DesignScreen | null {
    if (this.templateScreenCache.has(template)) return this.templateScreenCache.get(template) ?? null;
    const { document } = parseDesignDocument(JSON.stringify(template));
    const screen = document ? screensOf(document)[0] ?? null : null;
    this.templateScreenCache.set(template, screen);
    return screen;
  }

  templateThumbnail(template: Record<string, unknown>): string {
    const cached = this.templateThumbnailCache.get(template);
    if (cached) return cached;
    const thumbnail = templateThumbnailDataUrl(template);
    this.templateThumbnailCache.set(template, thumbnail);
    return thumbnail;
  }

  setLeftTab(tab: 'components' | 'icons'): void {
    this.leftTab.set(tab);
  }

  /** Icon on the selected node, so the library can mark it as current. */
  readonly selectedIconName = computed(() => this.doc.selectedNode()?.icon?.name ?? null);

  /**
   * Clicking an icon in the library **re-skins the selected icon node** when
   * there is one — the common case is "that's not quite the right glyph". With
   * nothing suitable selected it drops a new icon in the middle of the view
   * instead, so a click is never a no-op.
   */
  onIconPicked(entry: { name: string; label: string }): void {
    const selected = this.doc.selectedNode();
    if (selected?.type === 'ICON' && selected.icon) {
      this.doc.updateNode(
        selected.id,
        { icon: { ...selected.icon, name: entry.name }, name: entry.label },
        'Change icon',
      );
      return;
    }
    this.canvasRef()?.insertIconAtCenter(entry.name, entry.label);
  }

  togglePreview(): void {
    this.runtime.closePopupScreen();
    if (this.previewMode()) {
      this.previewMode.set(false);
      // Back to editing: the canvas shows the design's own data again.
      this.runtime.dropRefreshedData();
      return;
    }
    const screens = this.screens();
    if (!screens.length) {
      this.notify('Nothing to preview. Add a Frame or place content on the canvas first.');
      return;
    }
    // Popup screens open from their buttons; Preview starts on a page screen.
    this.previewScreenId.set(this.screenIdForSelection() ?? (pageScreensOf(this.doc.document())[0] ?? screens[0]).id);
    this.doc.clearSelection();
    this.previewMode.set(true);
    // Preview is the dashboard as it will run: read the database again rather
    // than show the rows saved at the last Generate / Update Configuration.
    // Quietly - each widget keeps what it shows until its new data lands.
    this.runtime.refreshDashboard(false, { quiet: true });
    // ECharts instances listen for window resize. Preview mounts them in a new
    // full-screen host, so notify them once that host exists and has dimensions.
    afterNextRender(() => {
      this.measurePreviewInset();
      window.dispatchEvent(new Event('resize'));
    }, { injector: this.injector });
  }

  /**
   * Put the Preview overlay right under the app's floating top bar (and to the
   * right of the project rail, when there is one), measured rather than
   * assumed: the overlay is `position: fixed`, but when an ancestor is its
   * containing block its `top` counts from that ancestor, and a fixed toolbar
   * offset left a blank band. The overlay's own top with no offset is read
   * back, and only the shortfall to the bar's bottom is added.
   */
  private measurePreviewInset(): void {
    const root = this.builderRoot()?.nativeElement;
    if (!root || !this.previewMode()) return;
    const current = this.previewInset();
    const box = root.getBoundingClientRect();
    // The offset ACTUALLY applied in the DOM, not the signal: this runs twice
    // on open (directly, then from the resize it dispatches), and the second
    // run can come before the new offset is rendered. Subtracting the signal's
    // value then counted it twice and pushed the page up behind the bar.
    const appliedTop = parseFloat(root.style.top) || 0;
    const appliedLeft = parseFloat(root.style.left) || 0;
    const baseTop = box.top - appliedTop;
    const baseLeft = box.left - appliedLeft;

    const bar = document.querySelector('mat-toolbar')?.getBoundingClientRect();
    const barBottom = bar && bar.height > 0 && bar.top < window.innerHeight / 2 ? bar.bottom + 4 : 0;
    const rail = document.querySelector('.project-mode-rail')?.getBoundingClientRect();
    const railRight = rail && rail.width > 0 && rail.left < window.innerWidth / 2 ? rail.right : 0;

    const next = {
      // May be NEGATIVE: when the containing ancestor starts below the bar,
      // the overlay has to move UP to meet it. Clamping at 0 left a blank band
      // between the bar and the page.
      top: Math.round(barBottom - baseTop),
      left: Math.max(0, Math.round(railRight - baseLeft)),
    };
    if (next.top !== current.top || next.left !== current.left) this.previewInset.set(next);
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.previewMode()) this.measurePreviewInset();
  }

  setName(name: string): void {
    this.doc.setName(name);
  }

  zoomIn(): void { this.view.zoomIn(); }
  zoomOut(): void { this.view.zoomOut(); }
  zoomReset(): void { this.view.setZoom(1); }
  zoomFit(): void { this.canvasRef()?.zoomToFit(); }

  newDesign(): void {
    this.doc.resetDocument(createEmptyDocument());
    this.persistedDesignName.set(null);
    this.view.reset();
    afterNextRender(() => this.view.fitToWidth(), { injector: this.injector });
    this.persistence.clearDraft();
  }

  // ── Persistence ────────────────────────────────────────────────────────────

  save(): void {
    if (this.saving()) return;
    this.saving.set(true);
    this.persistence
      .save(this.doc.document())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (saved) => {
          this.saving.set(false);
          // Adopt any id the backend assigned, without clearing history.
          if (saved.id !== this.doc.document().id) this.doc.resetDocument(saved);
          this.persistedDesignName.set(saved.name);
          this.rememberTemplate(saved);
          this.notify('Design saved to database');
        },
        error: (error: unknown) => {
          this.saving.set(false);
          console.error('[design-builder] save failed:', backendFailureMessage('Could not save the design to the database', error), error);
          this.notify('Save failed — the design could not be saved. Please try again.', 6000);
        },
      });
  }

  saveAs(): void {
    if (this.saving()) return;
    const source = this.doc.document();
    const currentName = source.name.trim() || 'Untitled design';
    const persistedName = this.persistedDesignName();
    // A renamed title is already an explicit Save As name. Otherwise ask so a
    // copy can never silently appear with the source dashboard's name.
    if (!persistedName || currentName === persistedName) {
      this.dialog.open(DesignSaveAsDialogComponent, {
        width: '440px',
        maxWidth: 'calc(100vw - 32px)',
        autoFocus: false,
        restoreFocus: true,
        disableClose: false,
        data: { currentName, suggestedName: `${currentName} Copy` },
      }).afterClosed().pipe(takeUntil(this.destroy$)).subscribe((newName) => {
        if (!newName) return;
        const trimmed = newName.trim();
        if (!trimmed || trimmed === persistedName) return;
        this.saveAsCopy(source, trimmed);
      });
      return;
    }

    this.saveAsCopy(source, currentName);
  }

  private saveAsCopy(source: DesignDocument, newName: string): void {
    const copy = this.copyForSaveAs(source, newName);
    this.saving.set(true);
    this.persistence.save(copy).pipe(takeUntil(this.destroy$)).subscribe({
      next: (saved) => {
        this.saving.set(false);
        this.doc.resetDocument(saved);
        this.persistedDesignName.set(saved.name);
        this.rememberTemplate(saved);
        this.notify(`Saved as "${saved.name}"`);
      },
      error: () => {
        this.saving.set(false);
        this.notify('Could not save the new dashboard. Please try Save As again.');
      },
    });
  }

  private copyForSaveAs(source: DesignDocument, name: string): DesignDocument {
    const now = new Date().toISOString();
    const copy = JSON.parse(JSON.stringify(source)) as DesignDocument;
    copy.id = createDocumentId();
    copy.name = name;
    copy.slug = slugify(name);
    copy.meta = {
      ...copy.meta,
      createdAt: now,
      updatedAt: now,
      status: 'draft',
    };
    delete copy.meta.publishedAt;
    return copy;
  }

  private rememberTemplate(document: DesignDocument): void {
    const list = this.templates();
    const index = list.findIndex((template) => template['id'] === document.id);
    this.templates.set(index >= 0
      ? list.map((template, itemIndex) => itemIndex === index ? document as unknown as Record<string, unknown> : template)
      : [document as unknown as Record<string, unknown>, ...list]);
  }

  /** Root frames = publishable screens. */
  readonly screens = computed(() => {
    this.doc.revision();
    return screensOf(this.doc.document());
  });

  readonly previewScreen = computed(() => {
    const screens = this.screens();
    return screens.find((screen) => screen.id === this.previewScreenId()) ?? pageScreensOf(this.doc.document())[0] ?? null;
  });

  readonly hasSample = computed(() => {
    this.doc.revision();
    return hasSampleData(this.doc.document());
  });

  /**
   * Publish to the platform `dashboard_layouts` table — the same call and the
   * same envelope the DVM Layout Builder uses, so a design lands beside every
   * other published dashboard rather than in a parallel store.
   *
   * Saves first: a published row that disagrees with the saved design is worse
   * than a failed publish.
   */
  async publish(): Promise<void> {
    const screens = this.screens();
    if (!screens.length) {
      this.notify('Nothing to publish — draw a Frame to define a screen first.');
      return;
    }
    if (this.unboundCount() && !window.confirm(
      `${this.unboundCount()} component${this.unboundCount() === 1 ? '' : 's'} still have no data and will publish empty. Publish anyway?`,
    )) return;

    // Captured before the save, which can reset the document: an already
    // published dashboard must be updated in place, never inserted again.
    const isUpdate = this.status() === 'published';
    this.saving.set(true);
    // Which of the two backend writes failed — they live on different hosts
    // (shape-builder template store vs platform dashboard_layouts), so one
    // generic message sent people looking at the wrong server.
    let stage: 'save' | 'publish' = 'save';
    try {
      const saved = await firstValueFrom(this.persistence.save(this.doc.document()));
      if (saved.id !== this.doc.document().id) this.doc.resetDocument(saved);
      this.persistedDesignName.set(saved.name);
      stage = 'publish';

      const name = saved.name.trim() || 'Untitled dashboard';
      // One id for the row and the URL, so a republish updates in place.
      const dashboardId = publishedDashboardId(saved);
      const layoutJson = buildPublishedLayoutJson(saved);
      // A rolled-back commit still answers 200 — check the body, not the status.
      const response = await firstValueFrom(
        this.dashboardService.publishLayoutToApi(layoutJson, await this.userEmail(), dashboardId, isUpdate),
      );
      if (isPlatformCommitFailure(response)) throw new Error('The server did not save the dashboard', { cause: response });

      this.doc.setStatus('published');

      // Same post-publish sequence as the DVM Layout Builder, so a design
      // lands in the sidenav's Dashboards list and opens like any other.
      const slug = dashboardSlug(dashboardId);
      writeDynamicDashboardCache(slug, layoutJson);
      markDashboardJustPublished(slug, layoutJson);
      this.dashboardService.invalidateOwnedRows();
      window.dispatchEvent(new CustomEvent('dvm:dashboard-published', {
        detail: { dashboardId, name },
      }));

      this.notify(`Published "${name}" — opening it now.`);
      this.router.navigate(['/dvm-published', slug]);
    } catch (error) {
      // The server's own error can echo the whole layout JSON back, which
      // swamped the screen as a snackbar. Full detail goes to the console; the
      // user gets one readable line.
      console.error('[design-builder] publish failed:', publishFailureMessage(stage, error), error);
      const action = this.status() === 'published' ? 'Update' : 'Publish';
      this.notify(
        stage === 'save'
          ? `${action} failed — the design could not be saved. Please try again.`
          : `${action} failed — the dashboard could not be published. Please try again.`,
        6000,
      );
    } finally {
      this.saving.set(false);
    }
  }

  private async userEmail(): Promise<string> {
    try {
      if (!(await this.keycloak.isLoggedIn())) return '';
      return (await this.keycloak.loadUserProfile()).email || this.tokenEmail();
    } catch {
      return this.tokenEmail();
    }
  }

  /** The email straight off the login token — no network call. */
  private tokenEmail(): string {
    try {
      return String(this.keycloak.getKeycloakInstance()?.tokenParsed?.['email'] ?? '');
    } catch {
      return '';
    }
  }

  // ── Layers panel ───────────────────────────────────────────────────────────

  onLayerSelect({ id, additive }: { id: string; additive: boolean }): void {
    if (additive) this.doc.toggleSelected(id);
    else this.doc.select(id);
    // Drawing next goes into the band the selection lives in.
    const node = locateNode(this.nodes(), id)?.node;
    if (node?.layerId) this.doc.setActiveLayer(node.layerId);
  }

  /**
   * Clicking a layer header selects everything in that band and arms it, so
   * the next node drawn is filed there.
   */
  onLayerBandSelect(layerId: string): void {
    this.doc.setActiveLayer(layerId);
    this.doc.selectLayerNodes(layerId);
  }

  onLayerReorder({ dragId, targetId, position }: LayerDropEvent): void {
    const nodes = this.nodes();
    if (position === 'inside') {
      this.doc.reparent(dragId, targetId);
      return;
    }
    const target = locateNode(nodes, targetId);
    if (!target) return;
    // The tree lists top-of-stack first, so "before" in the list is a HIGHER
    // index in the array. Converting here keeps the panel free of that detail.
    const index = position === 'before' ? target.index + 1 : target.index;
    this.doc.reparent(dragId, target.parent?.id ?? null, index);
  }

  // Dataset loading and the bind/configure handlers lived here. They belong to
  // the DATA TIER, which comes after the design pass — see the two-tier split
  // in docs/DESIGN_BUILDER.md. Removed rather than left as dead scaffolding.

  // ── Keyboard ───────────────────────────────────────────────────────────────

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const key = event.key.toLowerCase();

    // In Preview, Esc exits even with focus in a dashboard control (a dropdown's
    // search box, a slider input) — unless it is closing that control's panel.
    if (this.previewMode()) {
      if (key === 'escape' && !document.querySelector('.ng-dropdown-panel, .cdk-overlay-pane .mat-mdc-select-panel')) {
        event.preventDefault();
        this.togglePreview();
      }
      return;
    }

    if (isTypingTarget(event.target)) return;

    const mod = event.ctrlKey || event.metaKey;

    if (mod) {
      switch (key) {
        case 'z':
          event.preventDefault();
          event.shiftKey ? this.doc.redo() : this.doc.undo();
          return;
        case 'y':
          event.preventDefault();
          this.doc.redo();
          return;
        case 'd':
          event.preventDefault();
          this.doc.duplicateSelected();
          return;
        case 'g':
          event.preventDefault();
          event.shiftKey ? this.doc.ungroupSelected() : this.doc.groupSelected();
          return;
        case 'a':
          event.preventDefault();
          this.doc.selectAll();
          return;
        case 's':
          event.preventDefault();
          this.save();
          return;
        case ']':
          event.preventDefault();
          this.reorderSelection('forward');
          return;
        case '[':
          event.preventDefault();
          this.reorderSelection('backward');
          return;
        case '0':
          event.preventDefault();
          this.zoomFit();
          return;
        case '1':
          event.preventDefault();
          this.zoomReset();
          return;
        case "'":
          event.preventDefault();
          this.toggleLayoutGrid();
          return;
      }
      return;
    }

    if (key === 'delete' || key === 'backspace') {
      event.preventDefault();
      this.doc.deleteSelected();
      return;
    }

    if (key === 'escape') {
      this.doc.clearSelection();
      this.view.resetTool();
      return;
    }

    // Arrow nudge — 1px, or 10px with Shift.
    const step = event.shiftKey ? 10 : 1;
    const nudges: Record<string, [number, number]> = {
      arrowleft: [-step, 0],
      arrowright: [step, 0],
      arrowup: [0, -step],
      arrowdown: [0, step],
    };
    if (nudges[key]) {
      event.preventDefault();
      this.doc.nudgeSelected(...nudges[key]);
      return;
    }

    if (TOOL_KEYS[key]) {
      this.view.setTool(TOOL_KEYS[key]);
    }
  }

  private reorderSelection(command: 'forward' | 'backward'): void {
    for (const id of this.selectedIds()) this.doc.reorder(id, command);
  }

  /** Preview the selected frame (or the frame containing the selected child). */
  private screenIdForSelection(): string | null {
    const selectedId = this.selectedIds().values().next().value as string | undefined;
    if (!selectedId) return null;
    const contains = (node: DesignNode): boolean =>
      node.id === selectedId || !!node.children?.some(contains);
    for (const screen of this.screens()) {
      if (screen.id === selectedId || screen.nodes.some(contains)) return screen.id;
    }
    return null;
  }

  private notify(message: string, duration = 3000): void {
    this.snackBar.open(message, 'Dismiss', { duration });
  }
}

/**
 * A publish failure message that names the step and the server's own words.
 * `save` is the shape-builder template store (`environment.URL`), `publish`
 * the platform `dashboard_layouts` write (`apiEndpoints.baseUrl`).
 */
function publishFailureMessage(stage: 'save' | 'publish', error: unknown): string {
  const where = stage === 'save' ? 'Could not save the design to the template store' : 'Could not publish the dashboard';
  return backendFailureMessage(where, error);
}

/** `where` + the HTTP status / server message / "could not be reached", whichever applies. */
function backendFailureMessage(where: string, error: unknown): string {
  const http = error as { status?: number; statusText?: string; url?: string | null; error?: unknown; message?: string } | null;
  if (http && typeof http === 'object' && typeof http.status === 'number') {
    const body = http.error as { message?: string; error?: string } | string | null | undefined;
    const detail = typeof body === 'string' ? body : body?.message || body?.error || http.statusText || '';
    const reason = http.status === 0 ? 'the server could not be reached' : `HTTP ${http.status}${detail ? `: ${detail}` : ''}`;
    return `${where} — ${reason}${http.url ? ` (${http.url})` : ''}.`;
  }
  const cause = (error as { cause?: { error?: unknown; commitStatus?: unknown } } | null)?.cause;
  if (cause && typeof cause === 'object') {
    const detail = typeof cause.error === 'string' ? cause.error : cause.commitStatus ? String(cause.commitStatus) : '';
    return `${where} — the server rejected the write${detail ? ` (${detail})` : ''}.`;
  }
  if (cause === null) return `${where} — the platform API did not answer (check apiEndpoints.baseUrl and the backend).`;
  return `${where} — ${(error as { message?: string } | null)?.message || 'the server did not save it'}.`;
}

function isTypingTarget(target: EventTarget | null): boolean {
  const element = target as HTMLElement | null;
  if (!element) return false;
  const tag = element.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || element.isContentEditable;
}

/** How many widgets are still on generated sample data. */
function countSampleWidgets(document: DesignDocument): number {
  let count = 0;
  const walk = (nodes: readonly DesignNode[]): void => {
    for (const node of nodes) {
      if (node.widget?.sample) count += 1;
      if (node.children?.length) walk(node.children);
    }
  };
  for (const page of document.pages) walk(page.nodes);
  return count;
}

/**
 * A nested table rebuilt from the Data tab: drop the Design-tab header / title
 * / order overrides the new build changed, so the last edit wins.
 */
function supersedeNestedOverrides(widget: WidgetContent, tile: DashboardTile): Record<string, unknown> | undefined {
  const appearance = widget.appearance;
  const overrides = appearance?.['levelOverrides'];
  if (tile.layoutType !== 'NESTED_TABLE' || !Array.isArray(overrides)) return appearance;
  const levelsOf = (payload: unknown) => {
    const levels = (payload as Record<string, unknown> | undefined)?.['levels'];
    return Array.isArray(levels) ? levels : [];
  };
  const previous = levelsOf((widget.build as Record<string, unknown> | undefined)?.['payload']);
  return { ...appearance, levelOverrides: pruneSupersededOverrides(overrides, previous, levelsOf(tile.payload)) };
}
